import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Toaster, Position, Intent, Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';

import progressSvg from '../planview2/progress.svg';
import { isArray } from 'lodash';

import { Pannellum } from '../pannellum-react-local';
import PlanViewAdvanced2 from '../planview2/PlanViewAdvanced2';
import {
    PlanViewRepaintEvent,
    PLAN_VIEW_DOCUMENT_RENDERED_EVENT_TYPE
} from '../planview2/PlanViewAdvanced2.PlanViewEvents';
import TourPlayerDrawViewPoints from './TourPlayer.DrawViewpoints'
import { useAuth } from '../../contexts/auth';
import { Point2D, Vector2D } from '../planview2/GEMath';
import anime from 'animejs';


const pdfjsLib = require('pdfjs-dist/build/pdf');


const AppToaster = Toaster.create({
    position: Position.BOTTOM,
});


const TourPlayerSettings = {
    fadeSpeed:1000,
    stepSpeed:5000,
}


function TourPlayer(props, ref)
{
    const {
        zIndexBase,
        tour,
        designRoute,
        panoRoute,
        direction,
        finished
    } = props;

    const {
        keepalive,
        getProtectedResource
    } = useAuth();

    const imageCache = useRef({});

    const panoViewerDesign0 = useRef();
    const panoViewerDesign1 = useRef();
    const panoViewerPhoto0 = useRef();
    const panoViewerPhoto1 = useRef();

    const designViewerContainer0 = useRef();
    const designViewerContainer1 = useRef();

    const panoViewerContainer0 = useRef();
    const panoViewerContainer1 = useRef();

    const progressRef = useRef();
    const planViewContainerRef = useRef();
    const planViewRef = useRef();
    const planViewViewPointDrawerRef = useRef();
    const tourPlayerRef = useRef();
    const tourWalkDataRef = useRef({
        timeoutCallback: () => {},
        timeoutDelay:TourPlayerSettings.stepSpeed,
        timeoutId:-1,
    });


    const [ planDocument, setPlanDocument ] = useState({
        pdfDocument:null,
        pdfPage:null,
        pdfPageViewport:null,
        pdfOptionalContentConfig:null,
        pdfViewPointTrafo:[1,0,0,1,0,0]
    });


    const [ walk, setWalk ] = useState({
        step:-1,
        pause:false,
        designRoute:[],
        panoRoute:[],
        planRendered:false,

        viewPoints:[],

        pano0ZIndex:zIndexBase+3,
        pano1ZIndex:zIndexBase+2,

        designImageURL0:null,
        designImageURL1:null,

        panoImageURL0:null,
        panoImageURL1:null,

        designImageYaw0:0,
        designImageYaw1:0,

        panoImageYaw0:0,
        panoImageYaw1:0,

        designImage0OnLoad:() => {},
        designImage1OnLoad:() => {},
    });


    const suspendSyncingViewsRef = useRef(false);
    const lastYawRef = useRef(0);
    const lastPitchRef = useRef(0);


    const onPannellumRender = useCallback((srcPano, dstPano) => {
        if (walk.pause)
            return;
        if (walk.step === -1)
            return;
        if (walk.step >= walk.designRoute.length)
            return;

        if (suspendSyncingViewsRef.current)
            return;
        if (!srcPano.current)
            return;

        suspendSyncingViewsRef.current = true;

        const panoViewer = srcPano.current.getViewer();

        lastYawRef.current = panoViewer.getYaw();
        lastPitchRef.current = panoViewer.getPitch();

        if (dstPano.current) {
            dstPano.current.getViewer().lookAt(
                panoViewer.getPitch(),
                lastYawRef.current,
                panoViewer.getHfov(),
                false
            );
        }

        suspendSyncingViewsRef.current = false;
    }, [walk]);

/*
    const onRightPanoramaRender = useCallback(() => {
        if (suspendSyncingViewsRef.current)
            return;
        if (!leftPanoViewerRef.current)
            return;

        suspendSyncingViewsRef.current = true;

        const panoViewer = rightPanoViewerRef.current.getViewer();

        lastYawRef.current = panoViewer.getYaw();
        lastPitchRef.current = panoViewer.getPitch();

        if (leftPanoViewerRef.current) {
            leftPanoViewerRef.current.getViewer().lookAt(
                panoViewer.getPitch(),
                lastYawRef.current,
                panoViewer.getHfov(),
                false
            );
        }

        window.frustum.remove(1);

        const d = (dashboardDataRef.current.plan?.direction || 0) * 180.0 / Math.PI;

        let tyaw = 360 - (lastYawRef.current + d - 90);
        while (tyaw >= 360) tyaw -= 360;

        if (dashboardDataRef.current?.autodeskNavigationLocked)
            updateAutocadViewerCameraTarget(tyaw, lastPitchRef.current, panoViewer.getHfov());

        window.frustum.add(1, () => {
            onPanoYawChanged(tyaw);
        });

        window.frustum.draw();

        suspendSyncingViewsRef.current = false;
    }, [onPanoYawChanged]);
*/


    useEffect(() => {

        Promise.all([
            getProtectedResource(`/document.php?id=${tour.document_id}`, 'json'),
            getProtectedResource(`/document.php?content&id=${tour.document_id}`, 'arraybuffer')
        ])
            .then(results => {
                const [ documents, documentContent ] = results;

                const contentOk =  (documentContent && documentContent.data);
                const documentOk =  (documents && documents.data && isArray(documents.data.data));

                if (!documentOk || !contentOk)
                    return ;

                const document = documents.data.data[0];

                if (document.type.toLowerCase() === 'image/svg+xml')
                {
                    console.log('Eliminate SVG')
                    return ;
                }

                const loadingTask = pdfjsLib.getDocument(documentContent.data);

                loadingTask.promise
                    .then(async function (pdfDocument) {

                        const optionals = await pdfDocument.getOptionalContentConfig();

                        return pdfDocument.getPage(tour.page_index).then(function (page) {
                            //console.log(page)

                            const pageViewport = page.getViewport({scale:1,offsetX:0,offsetY:0});

                            //console.log(pageViewport)

                            setPlanDocument(old => ({
                                ...old,
                                pdfDocument:pdfDocument,
                                pdfPage:page,
                                pdfPageViewport:pageViewport,
                                pdfOptionalContentConfig:optionals,
                                pdfViewPointTrafo:document.viewpoint_trafo,
                            }));
                            // if (optionals) {
                            //     const order = optionals.getOrder();
                            //     if (order && order.length) {
                            //         const groups = order.map(id => ({
                            //             id:id,
                            //             name:optionals.getGroup(id).name,
                            //         }));
                            //
                            //         setLayers(groups);
                            //         setSelectedLayerIds(groups.map(g => g.id));
                            //     }
                            // }
                        });
                    })
                    .catch(function (reason) {
                        console.error("Error: " + reason);
                    });
            })
            .catch(err => console.error(err))
        ;

    }, [ tour, setPlanDocument ]);


    useEffect(() => {
        if (!planDocument.pdfDocument)
            return;

        if (designRoute?.length < 2 ||
            panoRoute?.length < 2 ||
            designRoute.length !== panoRoute.length)
        {
            if (designRoute?.length !== panoRoute?.length) {
                AppToaster.show({
                    message:'Tour is not properly set (length).',
                    intent:Intent.WARNING
                });
            }
            finished();
            return;
        }


        for (let i = 0; i < designRoute.length; ++i)
        {
            let i0 = i;
            if (i === designRoute.length - 1)
                i0 = i - 1;
            let i1 = i0 + 1;

            let p0 = new Point2D(designRoute[i0].x, designRoute[i0].y);
            let p1 = new Point2D(designRoute[i1].x, designRoute[i1].y);
            let v0 = p1.sub(p0);
            let a0 = v0.angle();

            const d0 = (direction || 0) * 180.0 / Math.PI;
            const d1 = a0 * 180.0 / Math.PI;
            let tyaw = 360 - (d1 + d0 - 90) + 180;

            while (tyaw >= 360) tyaw -= 360;

            designRoute[i].yaw = tyaw;
            panoRoute[i].yaw = tyaw;
        }

        const vpTr = new DOMMatrix(planDocument.pdfViewPointTrafo);
        const viewPoints = designRoute.map(d => {
            const vp = new DOMPoint(d.x, d.y).matrixTransform(vpTr);

            vp.id = d.id;
            vp.name = '';
            return vp;
        });

        const onRenderFinished = () => {
            planViewRef.current.removeEventListener(PLAN_VIEW_DOCUMENT_RENDERED_EVENT_TYPE, onRenderFinished);
            setWalk(old => ({
                ...old,
                step:0,
                pause:false,
                designRoute,
                panoRoute,
                viewPoints:viewPoints,
                designImageYaw0:designRoute[0].yaw,
                panoImageYaw0:panoRoute[0].yaw,
            }));
        };

        planViewRef.current.addEventListener(PLAN_VIEW_DOCUMENT_RENDERED_EVENT_TYPE, onRenderFinished);

    }, [designRoute, panoRoute, setWalk, planDocument])


    useEffect(() => {

        const onKeyDown = (evt) => {
            if (evt.code === "Escape" &&
                !evt.altKey &&
                !evt.ctrlKey &&
                !evt.shiftKey &&
                !evt.metaKey) {
                console.log('abort tour')

                clearTimeout(tourWalkDataRef.current.timeoutId);

                if (tourPlayerRef.current)
                    tourPlayerRef.current.style.opacity = 1;

                anime({
                    ...animeProps,
                    update: () => {
                        if (tourPlayerRef.current)
                            tourPlayerRef.current.style.opacity = fade.out;
                    }
                }).finished.then(finished);

                setWalk(old => ({
                    ...old,
                    step:-1,
                }));
            }
            else if (evt.code === "Space" &&
                !evt.altKey &&
                !evt.ctrlKey &&
                !evt.shiftKey &&
                !evt.metaKey) {

                clearTimeout(tourWalkDataRef.current.timeoutId);

                setWalk(old => ({
                    ...old,
                    pause:!old.pause
                }));
            }
        }

        const fade = {
            in:0,
            out:1,
        };

        const animeProps = {
            targets: fade,
            in:1,
            out: 0,
            round: 1000,
            duration: TourPlayerSettings.fadeSpeed,
            easing: 'easeInOutCubic',
        };

        if (walk.step === -1)
        {
            return;
        }
        if (walk.step >= walk.designRoute.length)
        {
            setTimeout(() => {
                if (tourPlayerRef.current)
                    tourPlayerRef.current.style.opacity = 1;

                anime({
                    ...animeProps,
                    update: () => {
                        if (tourPlayerRef.current)
                            tourPlayerRef.current.style.opacity = fade.out;
                    }
                }).finished.then(finished);

            }, TourPlayerSettings.stepSpeed);
            return;
        }


        keepalive();


        if (walk.pause) {
            document.addEventListener("keydown", onKeyDown);

            return () => {
                document.removeEventListener("keydown", onKeyDown)
            };
        }


        Promise.all([
            getProtectedResource(`/viewer.php?content&did=${walk.designRoute[walk.step].photo_id}`, 'arraybuffer'),
            getProtectedResource(`/viewer.php?content&did=${walk.panoRoute[walk.step].photo_id}`, 'arraybuffer')
            ])
            .then(responses => {

                const [designContent, panoContent] = responses;

                const blobD = new Blob([ designContent.data ]);
                const urlD = URL.createObjectURL(blobD);
                const blobP = new Blob([ panoContent.data ]);
                const urlP = URL.createObjectURL(blobP);

                planViewRef.current?.dispatchEvent(new PlanViewRepaintEvent());

                if ((walk.step % 2) === 0) {
                    tourWalkDataRef.current.timeoutDelay = walk.step === 0 ? 1000 : TourPlayerSettings.stepSpeed;
                    tourWalkDataRef.current.timeoutCallback = () => {
                        setWalk(old => ({
                            ...old,
                            step:old.step + 1,
                            pause:false,
                            pano0ZIndex:zIndexBase+3,
                            pano1ZIndex:zIndexBase+2,
                            designImageURL0:urlD,
                            panoImageURL0:urlP,
                            designImageYaw0:walk.designRoute[walk.step].yaw,
                            panoImageYaw0:walk.panoRoute[walk.step].yaw,
                            designImage0OnLoad: () => {
                                anime({
                                    ...animeProps,
                                    update: function() {
                                        if (old.step === 0) {
                                            progressRef.current.style.opacity = fade.out;
                                            planViewContainerRef.current.style.opacity = fade.in * 0.5;
                                        }
                                        else {
                                            designViewerContainer1.current.style.opacity = fade.out;
                                            panoViewerContainer1.current.style.opacity = fade.out;
                                        }
                                        designViewerContainer0.current.style.opacity = fade.in;
                                        panoViewerContainer0.current.style.opacity = fade.in;
                                    }
                                });
                            },
                            designImage1OnLoad: () => {}
                        }))
                    };
                }
                else {
                    tourWalkDataRef.current.timeoutDelay = TourPlayerSettings.stepSpeed;
                    tourWalkDataRef.current.timeoutCallback = () => {
                        setWalk(old => ({
                            ...old,
                            step:old.step + 1,
                            pause:false,
                            pano0ZIndex:zIndexBase+2,
                            pano1ZIndex:zIndexBase+3,
                            designImageURL1:urlD,
                            panoImageURL1:urlP,
                            designImageYaw1:walk.designRoute[walk.step].yaw,
                            panoImageYaw1:walk.panoRoute[walk.step].yaw,
                            designImage0OnLoad: () => {},
                            designImage1OnLoad: () => {
                                anime({
                                    ...animeProps,
                                    update: function() {
                                        designViewerContainer0.current.style.opacity = fade.out;
                                        panoViewerContainer0.current.style.opacity = fade.out;

                                        designViewerContainer1.current.style.opacity = fade.in;
                                        panoViewerContainer1.current.style.opacity = fade.in;
                                    }
                                });
                            }
                        }))
                    };
                }

                tourWalkDataRef.current.timeoutId = setTimeout(
                    tourWalkDataRef.current.timeoutCallback,
                    tourWalkDataRef.current.timeoutDelay
                );

                document.addEventListener("keydown", onKeyDown)
            })
            .catch(err => {
                console.log(err)
            })
        ;


        return () => {
            document.removeEventListener("keydown", onKeyDown)
        };
    }, [ walk, setWalk, finished, keepalive ]);


    useEffect(() => {
    }, [])


    return (
        <div ref={tourPlayerRef}
            id={'tour-player'}
            style={{
                //display: 'inherit',
                position: 'absolute',
                left:0,
                top:0,
                backgroundColor:'rgba(0,0,0,0.9)',
                right:0,
                bottom:0,
                zIndex:zIndexBase,
            }}
        >
            <div ref={progressRef}
                 style={{
                     position:'absolute',
                     left:0,
                     top:0,
                     right:0,
                     bottom:0,
                     display:'flex',
                     alignItems:'center',
                     justifyContent:'center',
                     //pointerEvents:'none',
                     opacity:1,
                     zIndex:zIndexBase+1,
                 }}
            >
                <img src={progressSvg}
                     width={48}
                     height={48}/>
            </div>
            {walk.pause &&
            <div style={{
                position:'absolute',
                left:'50%',
                top:32,
                //pointerEvents:'none',
                zIndex:zIndexBase + 20,
                backgroundColor:'rgba(0,0,0,0.3)',
                border:'1px solid rgba(255,255,255,0.5)',
                borderRadius:24,
                padding:8,
                transform:'translate(-50%, 0)'
            }}
            >
                <Icon icon={IconNames.PAUSE} size={32}/>
            </div>
            }
            <div ref={planViewContainerRef}
                 style={{
                     position:'absolute',
                     left:8,
                     bottom:8,
                     width:420,
                     height:297,
                     zIndex:zIndexBase+9,
                     opacity:0.0,
                     backgroundColor:'rgba(255,255,255,0)',
                     pointerEvents:'none'
                 }}
            >
                <PlanViewAdvanced2
                    ref={planViewRef}
                    pdfPage={planDocument.pdfPage}
                    pdfPageViewport={planDocument.pdfPageViewport}
                    pdfOptionalContentConfig={planDocument.pdfOptionalContentConfig}
                    pdfVisibleGroups={[]}
                    backgroundColor={'rgba(255,255,255,0)'}
                    width={'100%'}
                    height={'100%'}
                    zIndexBase={zIndexBase+10}
                >
                    <TourPlayerDrawViewPoints
                        ref={planViewViewPointDrawerRef}
                        planView={planViewRef.current}
                        viewPoints={walk.viewPoints}
                        step={walk.step}
                        zIndexBase={zIndexBase+11}
                    />
                </PlanViewAdvanced2>
            </div>
            <div style={{
                position:'absolute',
                left:0,
                top:0,
                right:0,
                bottom:0,
            }}>
                <div style={{position:'absolute', left:0, top:0, width:'calc(50% - 2px)', bottom:0}}>
                    <div ref={designViewerContainer0} style={{
                        position:'absolute',
                        left:0,
                        top:0,
                        right:0,
                        bottom:0,
                        zIndex:walk.pano0ZIndex,
                        opacity:0
                    }}>
                        <Pannellum
                            ref={panoViewerDesign0}
                            width="100%"
                            height="100%"
                            image={walk.designImageURL0 || "" }
                            //pitch={lastPitchRef.current}
                            yaw={walk.designImageYaw0}
                            hfov={90}
                            vaov={180}
                            friction={1.0}
                            showFullscreenCtrl={false}
                            showControls={false}
                            keyboardZoom={false}
                            disableKeyboardCtrl={true}
                            doubleClickZoom={false}
                            compass={false}
                            draggable={true}
                            autoLoad
                            onRender={() => onPannellumRender(panoViewerDesign0, panoViewerPhoto0)}
                            onLoad={() => {
                                URL.revokeObjectURL(walk.designImageURL0);
                                walk.designImage0OnLoad();
                            }}
                        />
                    </div>
                    <div ref={designViewerContainer1} style={{
                        position:'absolute',
                        left:0,
                        top:0,
                        right:0,
                        bottom:0,
                        zIndex:walk.pano1ZIndex,
                        opacity:0
                    }}>
                        <Pannellum
                            ref={panoViewerDesign1}
                            width="100%"
                            height="100%"
                            image={walk.designImageURL1 || ""}
                            //pitch={lastPitchRef.current}
                            yaw={walk.designImageYaw1}
                            hfov={90}
                            vaov={180}
                            friction={1.0}
                            showFullscreenCtrl={false}
                            showControls={false}
                            keyboardZoom={false}
                            disableKeyboardCtrl={true}
                            doubleClickZoom={false}
                            compass={false}
                            draggable={true}
                            autoLoad
                            onRender={() => onPannellumRender(panoViewerDesign1, panoViewerPhoto1)}
                            onLoad={() => {
                                URL.revokeObjectURL(walk.designImageURL1);
                                walk.designImage1OnLoad();
                            }}
                        />
                    </div>
                </div>
                <div style={{position:'absolute', top:0, width:'calc(50% - 2px)', right: 0, bottom:0}}>
                    <div ref={panoViewerContainer0}
                        style={{
                        position:'absolute',
                        left:0,
                        top:0,
                        right:0,
                        bottom:0,
                        zIndex:walk.pano0ZIndex,
                        opacity:0
                    }}>
                        <Pannellum
                            ref={panoViewerPhoto0}
                            width="100%"
                            height="100%"
                            image={walk.panoImageURL0 || ""}
                            //pitch={lastPitchRef.current}
                            yaw={walk.panoImageYaw0}
                            hfov={90}
                            vaov={180}
                            friction={1.0}
                            showFullscreenCtrl={false}
                            showControls={false}
                            keyboardZoom={false}
                            disableKeyboardCtrl={true}
                            doubleClickZoom={false}
                            compass={false}
                            draggable={true}
                            autoLoad
                            onRender={() => onPannellumRender(panoViewerPhoto0, panoViewerDesign0)}
                            onLoad={() => {
                                URL.revokeObjectURL(walk.panoImageURL0);
                            }}
                        />
                    </div>
                    <div ref={panoViewerContainer1}
                        style={{
                        position:'absolute',
                        left:0,
                        top:0,
                        right:0,
                        bottom:0,
                        zIndex:walk.pano1ZIndex,
                        opacity:0
                    }}>
                        <Pannellum
                            ref={panoViewerPhoto1}
                            width="100%"
                            height="100%"
                            image={walk.panoImageURL1 || ""}
                            //pitch={lastPitchRef.current}
                            yaw={walk.panoImageYaw1}
                            hfov={90}
                            vaov={180}
                            friction={1.0}
                            showFullscreenCtrl={false}
                            showControls={false}
                            keyboardZoom={false}
                            disableKeyboardCtrl={true}
                            doubleClickZoom={false}
                            compass={false}
                            draggable={true}
                            autoLoad
                            onRender={() => onPannellumRender(panoViewerPhoto1, panoViewerDesign1)}
                            onLoad={() => {
                                URL.revokeObjectURL(walk.panoImageURL1);
                            }}
                        />
                    </div>
                </div>
            </div>
            { !walk.pause &&
            <div style={{
                     position:'absolute',
                     left:0,
                     top:0,
                     right:0,
                     bottom:0,
                     zIndex:zIndexBase+20
                 }}
            >
            </div>
            }
        </div>);
}


TourPlayer.propTypes = {
    zIndexBase:PropTypes.number.isRequired,
    tour:PropTypes.object.isRequired,
    designRoute: PropTypes.array.isRequired,
    panoRoute: PropTypes.array.isRequired,
    direction: PropTypes.number.isRequired,
    finished: PropTypes.func.isRequired
}


TourPlayer.defaultProps = {
    direction:0,
}


export default React.forwardRef(TourPlayer);